import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

type Mode = "oom" | "elephant" | "waltz";

// Standard Oom-Pah on C major: bass alternates root (C2) and fifth above (G2);
// chord = C-E-G in the right hand. Two bars.
const oompah: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["C2"], caption: "Oom (C)" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
  { hand: "L", on: true, pitches: ["G2"], caption: "Oom (G)" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
  { hand: "L", on: true, pitches: ["C2"], caption: "Oom (C)" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
  { hand: "L", on: true, pitches: ["G2"], caption: "Oom (G)" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
];

// Elephant Oom-Pah / reggae: the fifth drops to G BELOW the root and arrives an eighth
// late, and BOTH chord stabs land off the beat — that offbeat skank is what makes it
// read as reggae rather than as a slow oom-pah. Eighth-note grid, so two bars = 16 cells.
// Written a bar at a time: cramming both repetitions into one bar put the second "Oom"
// on the &-of-3, left the bar ending on a left hand with no answering "Pa", and delayed
// only one of the two fifths — all three against this lesson's own rule that the hands
// alternate and never collide.
const elephantBar: RhythmCell[] = [
  { hand: "L", on: true,  pitches: ["C2"], caption: "Oom (C)" },          // beat 1
  { hand: "R", on: true,  pitches: ["C4", "E4", "G4"], caption: "Pa" },   // & of 1
  { hand: "L", on: false, pitches: [], caption: "—" },                    // beat 2 — the gap
  { hand: "L", on: true,  pitches: ["G1"], caption: "↓ G (late)" },       // & of 2
  { hand: "L", on: false, pitches: [], caption: "—" },                    // beat 3
  { hand: "R", on: true,  pitches: ["C4", "E4", "G4"], caption: "Pa" },   // & of 3 — the skank
  { hand: "L", on: false, pitches: [], caption: "—" },                    // beat 4
  { hand: "L", on: false, pitches: [], caption: "—" },                    // & of 4
];
const elephant: RhythmCell[] = [...elephantBar, ...elephantBar];

// Waltz: 3/4 — Oom · Pa · Pa
const waltz: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["C2"], caption: "Oom" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
  { hand: "R", on: true, pitches: ["C4", "E4", "G4"], caption: "Pa" },
];

export default function PianoFoundation19() {
  const { lesson, course, accent } = findLesson("piano-foundation", "19");
  const [mode, setMode] = useState<Mode>("oom");

  const grid = mode === "oom" ? oompah : mode === "elephant" ? elephant : waltz;

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF19-Oom-Pah-Waltz-Animated.mp4"
        caption={`Oom-Pah in 4/4 vs Waltz in 3/4 — same bass-then-chord trick, different bar length`}
        hint={`≈ 22 sec · march vs sway`}
      />
      <KeyHighlights>
        <Highlight><strong>Oom-Pah</strong> — in Jason's words, "arguably the most important or the most famous piano rhythm pattern". The two hands never collide — left hand stamps the bass on "Oom", right hand answers with the chord on "Pa".</Highlight>
        <Highlight>Left hand alternates between <strong>root and fifth</strong> of the chord. For C major: C and G. For F major: F and C. For G major: G and D.</Highlight>
        <Highlight>"Oom" is <strong>legato</strong> (longer, smoother). "Pa" is <strong>staccato</strong> (choppy, short). Same dynamic split as a kick drum and a snare.</Highlight>
        <Highlight>The <strong>elephant Oom-Pah</strong> drops the fifth <em>below</em> the root and delays it slightly — that's the reggae feel.</Highlight>
        <Highlight>For 3-beat songs (waltz), play <strong>Oom-Pah-Pah</strong>. Same two-hand mechanics; just one extra "Pa" per bar.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Arguably the most famous pattern" title="Why Oom-Pah matters" accent={accent.hex}>
        <Pull>
          In this lesson, we are going to learn arguably the most important or the most
          famous piano rhythm pattern and also the most scalable. Using this you can
          play a ton of genres from reggae to blues to rock and roll and so on.
        </Pull>

        <p>
          Oom-Pah is spread across two hands. They never collide. The left hand stamps a bass
          note while the right hand is silent; then the right hand stamps a chord while the
          left is silent. Left, right, left, right — like marching. Say it as you play:
          "<em>root, chord, fifth, chord, root, chord, fifth, chord</em>".
        </p>
      </Insight>

      <Insight eyebrow="The bass walk" title="Always root then fifth — same chord" accent={accent.hex}>
        <p>
          The left hand alternates between two bass notes per chord:{" "}
          <strong>the root</strong> and <strong>the fifth</strong> of the same chord:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>C major</strong> · root C, fifth G. Bass: C → G → C → G.</li>
          <li><strong>F major</strong> · root F, fifth C. Bass: F → C → F → C.</li>
          <li><strong>G major</strong> · root G, fifth D. Bass: G → D → G → D.</li>
        </ul>

        <p>
          The right hand stays parked on the chord — for C major: C-E-G. For F major: any
          inversion of F (we use C-F-A, an inversion). For G major: B-D-G or D-G-B,
          whichever's closest. The chord shape doesn't change between Oom and Pa; only the
          bass walks.
        </p>

        <KeyIdea>
          The "Oom" should always be longer (legato), the "Pa" always shorter (staccato).
          That single dynamic split is what gives Oom-Pah its bounce. If both feel the same
          length, the groove flattens.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Try the three modes" title="Standard · elephant · waltz" accent={accent.hex}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setMode("oom")} className={mode === "oom" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Oom-Pah · 4/4</button>
          <button onClick={() => setMode("elephant")} className={mode === "elephant" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Elephant · reggae</button>
          <button onClick={() => setMode("waltz")} className={mode === "waltz" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Waltz · 3/4</button>
        </div>

        <RhythmGrid
          cells={grid}
          subdivisions={mode === "elephant" ? 2 : 1}
          beats={mode === "waltz" ? 3 : 4}
          bpm={mode === "waltz" ? 132 : 96}
          label={
            mode === "oom" ? "Standard Oom-Pah · LH alternates C and G in the bass" :
            mode === "elephant" ? "Elephant / reggae · LH drops to G below and arrives late; both chords land off the beat" :
            "Waltz · 3/4 — Oom · Pa · Pa"
          }
        />
      </Insight>

      <Insight eyebrow="The Lion Sleeps Tonight" title="A I-IV-I-V loop in C major" accent={accent.hex}>
        <p>
          The first song demo: <strong>The Lion Sleeps Tonight</strong>. Four chords
          looping: <strong>C → F → C → G</strong> (one chord per bar). Sing along while the
          Oom-Pah engine runs underneath:
        </p>

        <p className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 font-display text-[15px] text-amber-200">
          "In the jungle, the mighty jungle, the lion sleeps tonight…"
        </p>

        <p>
          Verse 1: C major (in the jungle). Bar 2: F major (the mighty jungle). Bar 3: C
          major again (the lion sleeps). Bar 4: G major (tonight). Loop. Same Oom-Pah
          rhythm, three chord shapes flipping under it.
        </p>
      </Insight>

      <Insight eyebrow="The elephant variation" title="Drop the fifth below — and delay it" accent={accent.hex}>
        <p>
          A small change to standard Oom-Pah unlocks reggae. Instead of jumping <em>up</em>{" "}
          to the fifth above the root, jump <em>down</em> to the fifth below. And{" "}
          <strong>delay it slightly</strong> — let it land late.
        </p>

        <Pull>
          For some reason I call this the elephant Oom-Pah. Okay, I have no idea why,
          but just go with it.
        </Pull>

        <p>
          That late, low fifth is the signature lope of Bob Marley's piano. Listen to
          Buffalo Soldier (or any reggae piano part) and you'll hear the same delayed-bass
          pattern. The chord on top stays exactly the same; only the bass placement changes.
        </p>
      </Insight>

      <Insight eyebrow="The waltz" title="For songs in 3" accent={accent.hex}>
        <p>
          Some songs aren't in 4/4 — Happy Birthday, Tennessee Waltz, Amazing Grace. They're
          in 3/4, three beats per bar. Take the Oom-Pah pattern and add one more "Pa" per bar:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Oom · Pa · Pa  |  Oom · Pa · Pa  |  …
        </p>

        <Tip>
          For variety, make the first "Pa" staccato and the second "Pa" legato. Or vice
          versa. Small dynamic differences between the two upbeats give the waltz its
          rolling, dance-like motion.
        </Tip>
      </Insight>

      <Insight eyebrow="The natural way to practise" title="Sing first · let the piano follow" accent={accent.hex}>
        <Pull>
          The main way I would suggest to practise the Oom-Pah is a really natural
          organic approach. It's with you singing and just singing anything you want.
          Just let your mind wander and your piano has to wander with you like a friend,
          so to speak.
        </Pull>

        <p>
          Pick any tune you know. Sing it. Whenever the melody asks for a chord change, use
          Tonic, Pre-Dominant or Dominant (1, 4, 5 — the three chords from Lesson 13).
          Underneath, your hands just keep the Oom-Pah running. Three-chord folk and country
          songs work effortlessly: "Country Roads, take me home, to the place I belong…".
        </p>

        <Steps>
          <Step>Pick a chord — start with C major.</Step>
          <Step>Lock the LH bass: C-G-C-G. RH chord on every "Pa".</Step>
          <Step>Move to F (LH: F-C-F-C). Move to G (LH: G-D-G-D). Loop.</Step>
          <Step>Sing along. Add the elephant variation when you want a reggae feel. Add the waltz when the song is in 3.</Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-20"
        label="Lesson 20 · Explore the Blues on the Piano"
        why="Oom-Pah and Waltz cover folk, gospel, country, reggae, and dance. The next lesson opens up an entirely different world — the 12-bar blues."
      />
    </LessonShell>
  );
}
